import {Plugins, Expressions} from '@packages/systems/wfdl';
import {
  TJson,
  fromBoolean,
  fromTextRef,
  fromList,
  fromNameValuePairs,
} from '@packages/systems/plugins/Json';
import {map} from '@packages/systems/plugins/List';

import {
  functionOf,
  TTypeConstructor,
  ERecord,
  ECall,
  EList,
  EHole,
  TTypeReference,
  EUnion,
  TRecord,
  EFunction,
  TUnion,
  TText,
  ESelect,
  TList,
  EText,
  ECase,
} from '@packages/systems/wfdl-ast';
import {TEmail} from '@packages/systems/plugins/Email';
import {TBoolean} from '@packages/systems/plugins-utils/Boolean';
import {
  typeName,
  iconName,
  qualify,
  pluginName,
  type TypeName,
  type Qualified,
} from '@packages/systems/wfdl-Qualified';
import {TEffect, inviteUser} from '@packages/systems/plugins/Effect';
import {forall, _$, $} from '@packages/utilities/wfdl-helpers';

import {
  UserIdentifierDropdownType,
  viewUsers,
  addUserId,
  addUserEmail,
} from './UserIdentifierDropdownAlias';
import {UserSearchAliasType} from './UserSearchAlias';
import {AccessGroupsAliasType} from './AccessGroupsAlias';
import {InviteUser} from './InviteUser';
import {UpdateUser} from './UpdateUser';
import {InviteUserIcon, DeleteUserIcon, UpdateUserIcon} from './icons';

import {
  createInviteUserWFDLType,
  createDeleteUserWFDLType as UserRecordType,
} from './utils/createDataTypes';
export {
  createInviteUserWFDLType,
  createDeleteUserWFDLType,
} from './utils/createDataTypes';
import {LOGIC_BLOCK_CATEGORIES} from '@packages/systems/flows/shared';

const UsersPlugin = 'UsersPlugin';
const usersPluginName = pluginName(UsersPlugin);
const name = <nameType>(n: nameType): Qualified<nameType> =>
  qualify(usersPluginName, n);
export const usersTypeName: Qualified<TypeName> = name(typeName(UsersPlugin));

const error = TTypeReference('error');

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    InviteUserSmall: InviteUserIcon,
    InviteUserMedium: InviteUserIcon,
    InviteUserBlock: InviteUserIcon,
    DeleteUserSmall: DeleteUserIcon,
    DeleteUserMedium: DeleteUserIcon,
    DeleteUserBlock: DeleteUserIcon,
    UpdateUserSmall: UpdateUserIcon,
    UpdateUserMedium: UpdateUserIcon,
    UpdateUserBlock: UpdateUserIcon,
  },
  valuePresets: {
    inviteUserByEmailFactory: {
      factory: ECall({
        fun: Expressions.qualifiedVariable('UsersPlugin', 'Invite user'),
        arg: ERecord({
          Email: EHole(),
          AccessGroups: EList([]),
        }),
      }),
      label: 'Invite user',
      icon: {
        medium: name(iconName('InviteUserMedium')),
        small: name(iconName('InviteUserSmall')),
      },
      category: LOGIC_BLOCK_CATEGORIES.Actions,
    },
    deleteUserFactory: {
      factory: ECall({
        fun: Expressions.qualifiedVariable('UsersPlugin', 'Delete user'),
        arg: EUnion({
          tag: 'View users',
          value: EHole(),
        }),
      }),

      label: 'Delete user account',
      icon: {
        medium: name(iconName('DeleteUserMedium')),
        small: name(iconName('DeleteUserSmall')),
      },
      category: 'Actions',
    },

    updateUserFactory: {
      factory: ECall({
        fun: Expressions.qualifiedVariable('UsersPlugin', 'Update user'),
        arg: ERecord({
          'User Identifier': EUnion({
            tag: 'View users',
            value: EHole(),
          }),
          'Selected Fields': EList([]),
        }),
      }),

      label: 'Update user account',
      icon: {
        medium: name(iconName('UpdateUserMedium')),
        small: name(iconName('UpdateUserSmall')),
      },
      category: 'Actions',
    },
  },
  dataTypes: {
    UserIdentifierDropdownType,
    AccessGroupsAliasType,
    UserSearchAliasType,
    InviteUser,
    UpdateUser,
  },
  values: {
    'Invite user': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TTypeConstructor(name(typeName('InviteUser'))),
            TEffect(error, createInviteUserWFDLType())
          )
        ),
        exported: 'hidden',
      },
      value: inviteUser,
      icon: name(iconName('InviteUserBlock')),
    },
    'Delete user': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TTypeConstructor(name(typeName('UserIdentifierDropdownType'))),
            TEffect(error, UserRecordType())
          )
        ),
        exported: 'visible',
      },
      expression: EFunction({
        param: 'config',
        body: ECall({
          fun: _$('Effect', 'Low-level delete User'),
          arg: ECall({
            fun: _$('UsersPlugin', 'User identifier to record'),
            arg: $('config'),
          }),
        }),
      }),
      icon: name(iconName('DeleteUserBlock')),
    },
    'Update user': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TTypeConstructor(name(typeName('UpdateUser'))),
            TEffect(error, UserRecordType())
          )
        ),
        exported: 'hidden',
      },
      expression: EFunction({
        param: 'config',
        body: ECall({
          fun: _$('Effect', 'Low-level update User'),
          arg: ERecord({
            user: ECall({
              fun: _$('UsersPlugin', 'User identifier to record'),
              arg: ESelect({
                from: $('config'),
                prop: 'User Identifier',
              }),
            }),
            fieldsForUpdate: ECall({
              fun: _$('UsersPlugin', 'Get fields for update'),
              arg: ESelect({
                from: $('config'),
                prop: 'Selected Fields',
              }),
            }),
          }),
        }),
      }),
      icon: name(iconName('UpdateUserBlock')),
    },

    'Get fields for update': {
      envType: {
        dataType: functionOf(
          TList(
            TUnion({
              acceptCommunications: TBoolean,
              accessGroups: TList(TText),
            })
          ),
          TJson
        ),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      expression: EFunction({
        param: 'fields',
        body: fromNameValuePairs(
          map(
            EFunction({
              param: 'field',
              body: ECase({
                value: $('field'),
                cases: {
                  acceptCommunications: EFunction({
                    param: 'acceptCommunicationsVal',
                    body: ERecord({
                      Name: EText('acceptCommunications'),
                      Value: fromBoolean($('acceptCommunicationsVal')),
                    }),
                  }),
                  accessGroups: EFunction({
                    param: 'accessGroupsVal',
                    body: ERecord({
                      Name: EText('accessGroups'),
                      Value: fromList(map(fromTextRef, $('accessGroupsVal'))),
                    }),
                  }),
                },
              }),
            }),
            $('fields')
          )
        ),
      }),
    },

    'User identifier to record': {
      envType: {
        dataType: functionOf(
          TUnion({
            [addUserId]: TText,
            [addUserEmail]: TEmail,
            [viewUsers]: TRecord({name: TText, id: TText, email: TText}),
          }),
          TRecord({
            userIdentifier: TText,
            userIdentifierType: TText,
          })
        ),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      // @ts-expect-error - TS7031 - Binding element 'tag' implicitly has an 'any' type. | TS7031 - Binding element 'value' implicitly has an 'any' type.
      value: ({tag, val: value}) => {
        if (tag === addUserId) {
          return {
            userIdentifierType: 'USER_ID',
            userIdentifier: value,
          };
        } else if (tag === addUserEmail) {
          return {
            userIdentifierType: 'EMAIL',
            userIdentifier: value,
          };
        }
        // For "View users"
        else {
          return {
            userIdentifierType: 'USER_ID',
            userIdentifier: value.id,
          };
        }
      },
    },
  },
});
